/**
 * FILE: HistoryModal.js
 * PURPOSE: Your "Personal Voting Diary."
 * WHAT IT DOES: It shows a list of every poll you've ever voted on or created.
 * LAYMAN SUMMARY: A simple way to go back and see what you've done in the app.
 */
import React from 'react';
import { X } from 'lucide-react';
import { useModalA11y } from '../hooks/useModalA11y';

export function HistoryModal({ isOpen, onClose, darkMode, onSelectPoll, history = [] }) {
    const containerRef = useModalA11y(isOpen, onClose);
    if (!isOpen) return null;

    const displayHistory = history;

    if (displayHistory.length === 0) {
        return (
            <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={onClose}></div>
                <div ref={containerRef} role="dialog" aria-modal="true" aria-label="Activity History" className={`relative w-full max-w-2xl p-12 rounded-modal shadow-2xl border text-center ${darkMode ? 'bg-gray-900 border-gray-800 text-white' : 'bg-white border-gray-100 text-gray-900'}`}>
                    <h2 className="text-3xl font-black italic uppercase mb-4">No Activity Yet</h2>
                    <p className="text-gray-500 font-bold italic">Your verified records will appear here once you start voting.</p>
                    <button onClick={onClose} className="mt-8 px-8 py-3 bg-primary-600 text-white rounded-xl font-black uppercase text-xs tracking-widest hover:scale-105 transition-transform">Back to Feed</button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-md animate-in fade-in duration-300"
                onClick={onClose}
            ></div>

            {/* Modal Container */}
            <div ref={containerRef} role="dialog" aria-modal="true" aria-label="Activity History" className={`relative w-full max-w-2xl max-h-[80vh] rounded-modal shadow-2xl animate-in zoom-in-95 duration-300 border flex flex-col overflow-hidden ${darkMode ? 'bg-gray-900 border-gray-800 text-white' : 'bg-white border-gray-100 text-gray-900'
                }`}>

                {/* Header */}
                <div className="p-8 pb-6 flex justify-between items-center border-b border-gray-100 dark:border-gray-800">
                    <div>
                        <h2 className="text-3xl font-black italic uppercase tracking-tighter">Activity History</h2>
                        <p className="text-2xs font-black uppercase tracking-[0.2em] text-gray-500 mt-1">Your verified records</p>
                    </div>
                    <button onClick={onClose} aria-label="Close activity history" className="p-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
                        <X size={24} className="text-gray-500" />
                    </button>
                </div>

                {/* History List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-1 no-scrollbar">
                    {displayHistory.map((item) => (
                        <div
                            key={item.id}
                            onClick={() => {
                                onSelectPoll(item);
                                onClose();
                            }}
                            className={`group flex items-center justify-between p-4 rounded-2xl cursor-pointer transition-all ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50'
                                }`}
                        >
                            <div className="flex items-center space-x-4">
                                <div className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center text-lg group-hover:scale-110 transition-transform">
                                    {item.icon}
                                </div>
                                <div>
                                    <div className="text-sm font-bold group-hover:text-primary-600 transition-colors">
                                        {item.title}
                                    </div>
                                    <div className="text-2xs font-black uppercase tracking-widest text-gray-500">
                                        {item.action} • {item.category}
                                    </div>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-2xs font-black italic uppercase tracking-widest text-primary-600">{item.date}</div>
                                <div className="text-2xs font-bold text-gray-300 dark:text-gray-600 mt-1 uppercase">
                                    ID: {item.id.toUpperCase()}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50 flex justify-center">
                    <p className="text-2xs font-black text-gray-500 uppercase tracking-[0.3em]">
                        Total Contributions: {displayHistory.length}
                    </p>
                </div>
            </div>
        </div>
    );
}
